<template>
    <v-card v-if="message && draft" outlined class="review-card">
        <v-card-title class="review-header">
            <div class="min-width-0">
                <div class="overline primary--text">Segnalazione cliente</div>
                <h1 class="text-h6 text-sm-h5 text-truncate">{{ message.subject }}</h1>
                <div class="body-2 grey--text text--darken-1 mt-1">
                    {{ senderLabel }} · {{ sentAtLabel }}
                    <v-btn x-small text color="primary" class="ml-1" @click="copyText(message.sender && message.sender.email, 'Email copiata')">
                        <v-icon x-small left>mdi-content-copy</v-icon>Copia email
                    </v-btn>
                </div>
            </div>
            <v-spacer />
            <div class="d-flex align-center">
                <v-btn
                    v-if="isHandled"
                    small
                    outlined
                    color="primary"
                    :loading="statusChanging"
                    class="mr-2"
                    @click="$emit('change-status', { action: 'reopen' })"
                >
                    <v-icon small left>mdi-email-refresh-outline</v-icon>Riapri
                </v-btn>
                <v-btn
                    v-else-if="!(message.record && message.record.nc_number)"
                    small
                    outlined
                    color="error"
                    class="mr-2"
                    @click="handlingDialog = true"
                >
                    <v-icon small left>mdi-email-remove-outline</v-icon>Scarta mail
                </v-btn>
                <v-btn icon small :disabled="!hasPrevious" aria-label="Mail precedente" @click="$emit('select-previous')"><v-icon>mdi-chevron-up</v-icon></v-btn>
                <v-btn icon small :disabled="!hasNext" aria-label="Mail successiva" @click="$emit('select-next')"><v-icon>mdi-chevron-down</v-icon></v-btn>
            </div>
        </v-card-title>

        <mail-segnalazione-workflow
            :draft="draft"
            :dirty="draftDirty"
            :restored="draftRestored"
            @navigate="navigateToStep"
        />

        <div class="ownership-bar px-6 py-3">
            <div class="conversation-summary">
                <v-icon small color="blue-grey" class="mr-2">mdi-forum-outline</v-icon>
                <div>
                    <strong>{{ conversationCount }} messaggi nella conversazione</strong>
                    <div class="caption grey--text text--darken-1">
                        I follow-up condividono bozza e NC, evitando aperture duplicate.
                    </div>
                </div>
            </div>
            <v-select
                :value="draft.assignedTo"
                :items="operatorOptions"
                item-text="name"
                item-value="id"
                dense
                outlined
                hide-details
                clearable
                label="Responsabile"
                class="ownership-field"
                @input="updateOwnership($event, draft.slaDueAt)"
            />
            <v-text-field
                :value="slaInputValue"
                type="datetime-local"
                dense
                outlined
                hide-details
                clearable
                label="Scadenza SLA"
                class="ownership-field"
                @input="updateOwnership(draft.assignedTo, $event)"
            />
            <v-progress-circular v-if="savingDraft" indeterminate size="20" width="2" color="primary" />
            <v-icon v-else small color="success">mdi-cloud-check-outline</v-icon>
        </div>

        <nav class="review-nav px-6 py-2" aria-label="Sezioni della segnalazione">
            <v-btn x-small text color="primary" @click="scrollTo('customer-step')">Cliente</v-btn>
            <v-btn x-small text color="primary" @click="scrollTo('mail-content-title')">Mail</v-btn>
            <v-btn x-small text color="primary" @click="scrollTo('attachments-title')">Allegati</v-btn>
            <v-btn x-small text color="primary" @click="scrollTo('articles-title')">Articoli</v-btn>
            <v-btn
                v-if="message.record && message.record.nc_number"
                x-small
                text
                color="primary"
                @click="scrollTo('mail-eight-d-title')"
            >8D</v-btn>
            <v-btn x-small text color="primary" @click="scrollTo('customer-communications-title')">Comunicazioni</v-btn>
            <v-spacer />
            <v-btn x-small text color="grey darken-1" @click="resetDialog = true">
                <v-icon x-small left>mdi-restore</v-icon>Ripristina bozza
            </v-btn>
        </nav>

        <v-card-text class="review-body">
            <v-alert v-if="isHandled" type="info" text dense class="mt-4 mb-0">
                <strong>Mail scartata senza creare una NC.</strong>
                {{ handledReasonLabel }}
                <span v-if="message.record.handled_note"> · {{ message.record.handled_note }}</span>
                <span v-if="handledAuditLabel" class="caption d-block mt-1">{{ handledAuditLabel }}</span>
            </v-alert>

            <mail-segnalazione-proposal
                :recommendation="message.analysis && message.analysis.recommendation"
                :articles="draft.articles"
                @apply-suggestions="applySuggestions"
                @feedback="$emit('feedback', $event)"
            />
            <mail-segnalazione-shadow
                v-if="canCompareShadow"
                :key="String(message.uid)"
                :uid="String(message.uid)"
            />

            <mail-segnalazione-customer
                id="customer-step"
                :message="message"
                :draft="draft"
                :customer-options="customerOptions"
                :customer-search-loading="customerSearchLoading"
                :associating="associatingCustomer"
                :reanalyzing="reanalyzing"
                :addresses="addresses"
                :addresses-loading="addressesLoading"
                @search-customers="$emit('search-customers', $event)"
                @confirm-customer="$emit('confirm-customer', $event)"
                @update-address="updateDraftField('addressCode', $event)"
                @update-reference="updateDraftField('customerReference', $event)"
            />

            <v-divider />

            <section aria-labelledby="mail-content-title" class="review-section">
                <div class="section-heading">
                    <h2 id="mail-content-title" class="subtitle-1 font-weight-bold">Messaggio originale</h2>
                    <div>
                        <v-btn text small color="primary" @click="copyText(message.body_text, 'Testo della mail copiato')">
                            <v-icon small left>mdi-content-copy</v-icon>Copia testo
                        </v-btn>
                        <v-btn text small color="primary" @click="showFullText = !showFullText">
                            {{ showFullText ? "Riduci" : "Mostra tutto" }}
                        </v-btn>
                    </div>
                </div>
                <div :class="['mail-body', { collapsed: !showFullText }]">{{ message.body_text }}</div>
            </section>

            <mail-segnalazione-attachments
                :message="message"
                :selected-ids="draft.eightDEvidenceIds"
                @update:selected-ids="updateDraftField('eightDEvidenceIds', $event)"
                @classification-feedback="$emit('feedback', $event)"
            />

            <v-divider />

            <section aria-labelledby="articles-title" class="review-section">
                <div class="section-heading align-start">
                    <div>
                        <h2 id="articles-title" class="subtitle-1 font-weight-bold">Articoli e problemi</h2>
                        <div class="caption grey--text text--darken-1">
                            Sono proposti soltanto articoli consegnati a {{ draft.customerName || "questo cliente" }} negli ultimi 6 mesi.
                        </div>
                    </div>
                    <v-chip small color="orange lighten-4">8D da completare</v-chip>
                </div>

                <v-autocomplete
                    v-model="selectedArticle"
                    :items="articleOptions"
                    :loading="articleSearchLoading"
                    :search-input.sync="articleSearch"
                    item-text="label"
                    return-object
                    dense
                    outlined
                    clearable
                    hide-no-data
                    label="Aggiungi articolo"
                    prepend-inner-icon="mdi-magnify"
                    class="mt-4"
                    :disabled="!draft.customerConfirmed"
                    @change="addSelectedArticle"
                />

                <v-alert v-if="!draft.articles.length" dense text type="warning">
                    {{ message.analysis.detail || "Nessun articolo riconosciuto. Cercalo manualmente tra le consegne recenti." }}
                </v-alert>

                <v-card
                    v-for="(article, index) in draft.articles"
                    :key="article.code"
                    flat
                    outlined
                    class="article-card mb-4"
                >
                    <v-card-title class="py-3">
                        <div>
                            <div class="subtitle-1 font-weight-bold">{{ article.code }}</div>
                            <div class="caption grey--text text--darken-1">{{ article.description }}</div>
                            <div class="caption mt-1">
                                Consegnati: <strong>{{ article.deliveredQuantity || "n.d." }}</strong>
                                <span v-if="article.customerReferences && article.customerReferences.length">
                                    · Rif. cliente: {{ article.customerReferences.join(", ") }}
                                </span>
                            </div>
                            <div v-if="article.lastDelivery" class="caption mt-1 primary--text text--darken-2">
                                Ultima consegna: <strong>{{ article.lastDelivery.delivery_number }}</strong>
                                · {{ dateLabel(article.lastDelivery.date) }}
                                · {{ article.lastDelivery.quantity }} PZ
                            </div>
                        </div>
                        <v-spacer />
                        <v-chip x-small outlined class="mr-2">{{ confidenceLabel(article.confidence) }}</v-chip>
                        <v-btn x-small text color="success" @click="confirmArticle(article)">
                            <v-icon x-small left>mdi-check-decagram-outline</v-icon>Conferma codice
                        </v-btn>
                        <v-btn icon small :aria-label="`Rimuovi ${article.code}`" @click="removeArticle(index)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <div v-if="article.evidence && article.evidence.length" class="mb-3 evidence-row">
                            <v-chip
                                v-for="(evidence, evidenceIndex) in article.evidence"
                                :key="`${evidence.source}-${evidence.matched}-${evidenceIndex}`"
                                x-small
                                outlined
                                color="success"
                            >
                                {{ evidenceLabel(evidence) }}
                            </v-chip>
                        </div>
                        <v-expansion-panels v-if="article.deliveries && article.deliveries.length" flat class="mb-3">
                            <v-expansion-panel>
                                <v-expansion-panel-header class="px-0 py-2">
                                    Dettaglio DDT ultimi 6 mesi ({{ article.deliveries.length }})
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="delivery-panel">
                                    <v-simple-table dense>
                                        <thead><tr><th>DDT</th><th>Data</th><th>Q.tà</th><th>Ordine</th></tr></thead>
                                        <tbody>
                                            <tr v-for="delivery in article.deliveries" :key="`${delivery.delivery_number}-${delivery.sales_order_line}`">
                                                <td>{{ delivery.delivery_number }}</td>
                                                <td>{{ dateLabel(delivery.date) }}</td>
                                                <td>{{ delivery.quantity }}</td>
                                                <td>{{ delivery.sales_order || "—" }}</td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                        <v-textarea
                            :value="article.problemText"
                            outlined
                            dense
                            auto-grow
                            rows="3"
                            counter="2000"
                            maxlength="2000"
                            label="Problema segnalato per questo articolo"
                            :error-messages="problemError(article)"
                            @input="updateArticle(index, 'problemText', $event)"
                        />
                        <div class="d-flex justify-end mt-n3 mb-2">
                            <v-btn x-small text color="success" @click="confirmIssue(article)">
                                <v-icon x-small left>mdi-message-check-outline</v-icon>Conferma difetto
                            </v-btn>
                        </div>
                        <v-row dense>
                            <v-col v-if="article.suggestedQuantity" cols="12">
                                <v-alert dense text type="info" class="mb-1">
                                    Quantità proposta: <strong>{{ article.suggestedQuantity }} PZ</strong>
                                </v-alert>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field
                                    :value="article.quantity"
                                    outlined
                                    dense
                                    type="number"
                                    min="0.001"
                                    :max="article.deliveredQuantity || undefined"
                                    step="0.001"
                                    label="Quantità contestata"
                                    suffix="PZ"
                                    :error-messages="quantityError(article)"
                                    @input="updateArticle(index, 'quantity', $event)"
                                />
                            </v-col>
                            <v-col cols="12" sm="9">
                                <v-alert dense outlined type="info" class="mb-0 fixed-management">
                                    <strong>Causale fissa {{ fixedManagementCode }}</strong> · {{ fixedManagementDescription }}
                                    <div class="caption mt-1">
                                        Applicata automaticamente perché la NC nasce da una segnalazione senza DDT di reso.
                                    </div>
                                </v-alert>
                            </v-col>
                            <v-col cols="12">
                                <v-select
                                    :value="article.attachmentIds"
                                    :items="attachmentOptions"
                                    item-text="label"
                                    item-value="id"
                                    multiple
                                    chips
                                    small-chips
                                    outlined
                                    dense
                                    label="Allegati associati"
                                    @input="updateArticle(index, 'attachmentIds', $event)"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </section>

            <section v-if="prepareResult" aria-labelledby="prefill-title" class="review-section">
                <h2 id="prefill-title" class="subtitle-1 font-weight-bold">Controllo duplicati e bozza 8D</h2>
                <v-alert
                    v-if="prepareResult.duplicates && prepareResult.duplicates.length"
                    type="warning"
                    text
                    class="mt-3"
                >
                    <div class="font-weight-bold mb-2">Possibili NC già esistenti</div>
                    <div v-for="duplicate in prepareResult.duplicates" :key="`${duplicate.source}-${duplicate.nc_number}`">
                        <strong>{{ duplicate.nc_number }}</strong> · {{ dateLabel(duplicate.date) }} ·
                        {{ duplicate.reasons.join("; ") }}
                        <v-chip v-if="duplicate.open === true" x-small color="red lighten-4" class="ml-1">NC aperta</v-chip>
                    </div>
                </v-alert>
                <v-alert v-else type="success" dense text class="mt-3">
                    Nessuna NC simile trovata negli ultimi 60 giorni.
                </v-alert>
                <v-card v-if="prepareResult.eight_d_prefill" outlined class="prefill-card">
                    <v-card-text>
                        <div class="d-flex align-center mb-3">
                            <v-chip small color="blue lighten-4" class="mr-3">D1</v-chip>
                            <div>
                                <strong>Coordinatore proposto:</strong>
                                {{ prepareResult.eight_d_prefill.d1.coordinator || "da definire" }}
                                · Funzioni: {{ prepareResult.eight_d_prefill.d1.functions.join(", ") }}
                            </div>
                        </div>
                        <div
                            v-for="row in prepareResult.eight_d_prefill.d2"
                            :key="row.article_code"
                            class="d-flex align-start mb-2"
                        >
                            <v-chip small color="blue lighten-4" class="mr-3">D2</v-chip>
                            <div>
                                <strong>{{ row.article_code }}:</strong> {{ row.what }}
                                <div class="caption grey--text text--darken-1">
                                    {{ row.when }} · Estensione {{ row.extent }}
                                    <span v-if="row.last_delivery && row.last_delivery.delivery_number">
                                        · Ultimo DDT {{ row.last_delivery.delivery_number }} del {{ dateLabel(row.last_delivery.date) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="caption mt-3">{{ prepareResult.eight_d_prefill.notice }}</div>
                    </v-card-text>
                </v-card>
            </section>

            <v-divider />

            <mail-segnalazione-eight-d
                v-if="message.record && message.record.nc_number"
                :nc-number="message.record.nc_number"
                :rows="ncRows"
                :loading="ncRowsLoading"
                :error-message="ncRowsError"
                @refresh="$emit('refresh-nc-rows')"
            />

            <v-divider v-if="message.record && message.record.nc_number" />

            <mail-segnalazione-reply
                :message="message"
                :evidence-attachment-ids="draft.eightDEvidenceIds"
                :local-preview="localPreview"
                :sending="responding"
                :result="responseResult"
                :nc-number="message.record && message.record.nc_number"
                @send-response="$emit('send-response', $event)"
            />
        </v-card-text>

        <v-card-actions class="review-actions">
            <div>
                <div class="caption grey--text text--darken-1">Una mail genera una sola NC, anche quando contiene più articoli.</div>
                <div v-if="validationError" class="caption error--text mt-1" role="alert">
                    <v-icon x-small color="error">mdi-alert-circle-outline</v-icon> {{ validationError }}
                </div>
            </div>
            <v-spacer />
            <v-btn
                v-if="message.record && message.record.nc_number"
                outlined
                color="success"
                :to="{ path: '/qualita/riepologonc', query: { nc: message.record.nc_number } }"
            >
                <v-icon left>mdi-open-in-new</v-icon>
                Riepilogo NC {{ message.record.nc_number }}
            </v-btn>
            <v-btn
                v-else-if="isHandled"
                outlined
                color="primary"
                :loading="statusChanging"
                @click="$emit('change-status', { action: 'reopen' })"
            >
                <v-icon left>mdi-email-refresh-outline</v-icon>Riapri lavorazione
            </v-btn>
            <v-btn
                v-else-if="prepareResult"
                color="success"
                :loading="creating"
                @click="$emit('create-nc')"
            >
                <v-icon left>mdi-shield-check-outline</v-icon>
                Crea NC in Sage
            </v-btn>
            <v-btn
                v-else
                color="primary"
                :loading="preparing"
                :disabled="Boolean(validationError)"
                @click="$emit('prepare')"
            >
                <v-icon left>mdi-clipboard-check-outline</v-icon>
                Verifica e prepara NC
            </v-btn>
        </v-card-actions>

        <v-dialog v-model="resetDialog" max-width="520">
            <v-card>
                <v-card-title>Ripristinare la bozza?</v-card-title>
                <v-card-text>Le modifiche effettuate su cliente, articoli, quantità e allegati verranno sostituite dai dati rilevati nella mail.</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="resetDialog = false">Annulla</v-btn>
                    <v-btn color="warning" @click="confirmReset"><v-icon left>mdi-restore</v-icon>Ripristina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="handlingDialog" max-width="560" persistent>
            <v-card>
                <v-card-title>Scartare questa mail?</v-card-title>
                <v-card-text>
                    <v-alert type="warning" text dense>
                        Non verrà creata alcuna NC. La mail resterà consultabile tra le scartate e potrà essere riaperta.
                    </v-alert>
                    <v-select
                        v-model="handledReason"
                        :items="handledReasons"
                        outlined
                        dense
                        label="Motivo"
                    />
                    <v-textarea
                        v-model="handledNote"
                        outlined
                        dense
                        rows="2"
                        maxlength="500"
                        counter="500"
                        :label="handledReason === 'other' ? 'Nota obbligatoria' : 'Nota facoltativa'"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="handlingDialog = false">Annulla</v-btn>
                    <v-btn
                        color="error"
                        :loading="statusChanging"
                        :disabled="!handledReason || (handledReason === 'other' && !handledNote.trim())"
                        @click="confirmHandled"
                    >
                        Scarta mail
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>

    <v-card v-else outlined class="empty-review d-flex flex-column align-center justify-center text-center">
        <v-icon size="52" color="grey lighten-1">mdi-email-search-outline</v-icon>
        <h1 class="text-h6 mt-4">Seleziona una mail</h1>
        <p class="body-2 grey--text text--darken-1 mb-0">Qui potrai verificare testo, allegati e articoli associati.</p>
    </v-card>
</template>

<script>
import {
    addArticleToDraft,
    applyAutomaticSuggestions,
    draftValidation,
    MAIL_SIGNAL_MANAGEMENT_CODE,
    MAIL_SIGNAL_MANAGEMENT_DESCRIPTION,
} from "./mailSegnalazioniDraft.mjs";
import MailSegnalazioneCustomer from "./MailSegnalazioneCustomer.vue";
import MailSegnalazioneAttachments from "./MailSegnalazioneAttachments.vue";
import MailSegnalazioneEightD from "./MailSegnalazioneEightD.vue";
import MailSegnalazioneProposal from "./MailSegnalazioneProposal.vue";
import MailSegnalazioneShadow from "./MailSegnalazioneShadow.vue";
import MailSegnalazioneReply from "./MailSegnalazioneReply.vue";
import MailSegnalazioneWorkflow from "./MailSegnalazioneWorkflow.vue";

export default {
    name: "MailSegnalazioneReview",
    components: { MailSegnalazioneAttachments, MailSegnalazioneCustomer, MailSegnalazioneEightD, MailSegnalazioneProposal, MailSegnalazioneShadow, MailSegnalazioneReply, MailSegnalazioneWorkflow },
    props: {
        message: { type: Object, default: null },
        draft: { type: Object, default: null },
        articleOptions: { type: Array, default: () => [] },
        articleSearchLoading: { type: Boolean, default: false },
        preparing: { type: Boolean, default: false },
        responding: { type: Boolean, default: false },
        localPreview: { type: Boolean, default: true },
        responseResult: { type: Object, default: null },
        customerOptions: { type: Array, default: () => [] },
        customerSearchLoading: { type: Boolean, default: false },
        associatingCustomer: { type: Boolean, default: false },
        reanalyzing: { type: Boolean, default: false },
        addresses: { type: Array, default: () => [] },
        addressesLoading: { type: Boolean, default: false },
        prepareResult: { type: Object, default: null },
        creating: { type: Boolean, default: false },
        draftDirty: { type: Boolean, default: false },
        draftRestored: { type: Boolean, default: false },
        hasPrevious: { type: Boolean, default: false },
        hasNext: { type: Boolean, default: false },
        statusChanging: { type: Boolean, default: false },
        savingDraft: { type: Boolean, default: false },
        operators: { type: Array, default: () => [] },
        conversationMessages: { type: Array, default: () => [] },
        ncRows: { type: Array, default: () => [] },
        ncRowsLoading: { type: Boolean, default: false },
        ncRowsError: { type: String, default: "" },
    },
    data: () => ({
        showFullText: false,
        articleSearch: "",
        selectedArticle: null,
        fixedManagementCode: MAIL_SIGNAL_MANAGEMENT_CODE,
        fixedManagementDescription: MAIL_SIGNAL_MANAGEMENT_DESCRIPTION,
        resetDialog: false,
        handlingDialog: false,
        handledReason: "",
        handledNote: "",
        handledReasons: [
            { text: "Non è una segnalazione", value: "not_report" },
            { text: "Già gestita altrove", value: "already_handled" },
            { text: "Duplicata", value: "duplicate" },
            { text: "Solo informativa", value: "informational" },
            { text: "Altro", value: "other" },
        ],
    }),
    computed: {
        canCompareShadow() {
            const user = this.$store.getters.userdata || {};
            const groups = user.groups || [];
            const isAdmin = user.admin === true || user.is_superuser === true
                || (groups.includes("Amministrazione") && groups.includes("Qualità"));
            return isAdmin && /^[1-9][0-9]{0,19}$/.test(String(this.message?.uid || ""));
        },
        senderLabel() {
            return this.message.sender?.name || this.message.sender?.email || "Mittente sconosciuto";
        },
        sentAtLabel() {
            if (!this.message.sent_at) return "Data non disponibile";
            return new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(
                new Date(this.message.sent_at)
            );
        },
        attachmentOptions() {
            return (this.message.attachments || []).map((attachment) => ({
                id: attachment.id,
                label: attachment.filename,
            }));
        },
        validationError() {
            return draftValidation(this.draft);
        },
        isHandled() {
            return this.message?.workflow_status?.id === "handled";
        },
        handledReasonLabel() {
            const reason = this.handledReasons.find((item) => item.value === this.message?.record?.handled_reason);
            return reason?.text || "Motivo non indicato";
        },
        handledAuditLabel() {
            const parts = [];
            if (this.message?.record?.handled_by) parts.push(`da ${this.message.record.handled_by}`);
            if (this.message?.record?.handled_at) {
                parts.push(new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(
                    new Date(this.message.record.handled_at)
                ));
            }
            return parts.length ? `Registrata ${parts.join(" · ")}` : "";
        },
        operatorOptions() {
            return this.operators.map((operator) => ({ id: operator.id, name: operator.name }));
        },
        conversationCount() {
            return Math.max(Number(this.message?.conversation?.message_count || 1), this.conversationMessages.length || 1);
        },
        slaInputValue() {
            if (!this.draft?.slaDueAt) return "";
            const date = new Date(this.draft.slaDueAt);
            if (Number.isNaN(date.getTime())) return "";
            const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
            return local.toISOString().slice(0, 16);
        },
    },
    watch: {
        articleSearch(value) {
            this.$emit("search-articles", value || "");
        },
    },
    methods: {
        confidenceLabel(value) {
            return value === "alta" ? "Riconoscimento verificato" : "Associazione manuale";
        },
        evidenceSource(value) {
            if (value === "oggetto") return "Oggetto";
            if (value === "testo mail") return "Testo";
            if (value === "nome allegato") return "Nome allegato";
            if (value === "contenuto allegato") return "Allegato";
            return value;
        },
        evidenceLabel(evidence) {
            const parts = [this.evidenceSource(evidence.source)];
            if (evidence.filename) parts.push(evidence.filename);
            if (evidence.page) parts.push(`pag. ${evidence.page}`);
            if (evidence.extraction === "pdf_ocr" || evidence.extraction === "image_ocr") parts.push("OCR");
            parts.push(evidence.matched);
            return parts.join(" · ");
        },
        dateLabel(value) {
            if (!value) return "data n.d.";
            return new Intl.DateTimeFormat("it-IT").format(new Date(value));
        },
        problemError(article) {
            return String(article.problemText || "").trim() ? [] : ["Descrivi il problema segnalato."];
        },
        quantityError(article) {
            if (Number(article.quantity) <= 0) return ["Indica una quantità maggiore di zero."];
            if (Number(article.deliveredQuantity) > 0 && Number(article.quantity) > Number(article.deliveredQuantity)) {
                return [`Supera i ${article.deliveredQuantity} pezzi consegnati negli ultimi 6 mesi.`];
            }
            return [];
        },
        updateOwnership(assignedTo, slaDueAt) {
            this.$emit("update-ownership", {
                assignedTo: assignedTo || null,
                slaDueAt: slaDueAt ? new Date(slaDueAt).toISOString() : null,
            });
        },
        confirmArticle(article) {
            this.$emit("feedback", {
                kind: "article",
                predicted_value: article.confidence === "manuale" ? "" : article.code || "",
                corrected_value: article.code || "",
                accepted: true,
                context: { evidence: article.evidence || [], source: article.confidence || "" },
            });
        },
        confirmIssue(article) {
            this.$emit("feedback", {
                kind: "issue",
                predicted_value: article.problemText || "",
                corrected_value: article.problemText || "",
                accepted: true,
                context: { article_code: article.code, categories: article.issueCategories || [] },
            });
        },
        applySuggestions() {
            this.$emit("update-draft", applyAutomaticSuggestions(this.draft));
            this.$toast.success("Quantità proposta applicata. Verificala prima di creare la NC.", {
                icon: "mdi-auto-fix",
                timeout: 4500,
            });
        },
        confirmHandled() {
            this.$emit("change-status", {
                action: "discard",
                reason: this.handledReason,
                note: this.handledNote,
            });
            this.handlingDialog = false;
        },
        async copyText(value, successMessage) {
            if (!value) return;
            try {
                await navigator.clipboard.writeText(String(value));
                this.$toast.success(successMessage, { icon: "mdi-content-copy", timeout: 2500 });
            } catch (_error) {
                this.$toast.error("Copia non disponibile nel browser.", { icon: "mdi-alert", timeout: 3000 });
            }
        },
        navigateToStep(step) {
            const target = step === "customer" || step === "address" ? "customer-step" : "articles-title";
            this.scrollTo(target);
        },
        scrollTo(id) {
            this.$nextTick(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
        },
        confirmReset() {
            this.resetDialog = false;
            this.$emit("reset-draft");
        },
        updateDraftField(field, value) {
            this.$emit("update-draft", { ...this.draft, [field]: value });
        },
        addSelectedArticle(article) {
            if (!article) return;
            const next = addArticleToDraft(this.draft, article, this.message.body_text || "");
            this.$emit("update-draft", next);
            this.$emit("feedback", {
                kind: "article",
                predicted_value: "",
                corrected_value: article.code,
                accepted: null,
                context: { source: "manual_addition" },
            });
            this.selectedArticle = null;
            this.articleSearch = "";
        },
        removeArticle(index) {
            const removed = this.draft.articles[index];
            const next = { ...this.draft, articles: this.draft.articles.filter((_item, itemIndex) => itemIndex !== index) };
            this.$emit("update-draft", next);
            if (removed) {
                this.$emit("feedback", {
                    kind: "article",
                    predicted_value: removed.code,
                    corrected_value: "",
                    accepted: false,
                    context: { evidence: removed.evidence || [], source: removed.confidence || "" },
                });
            }
        },
        updateArticle(index, field, value) {
            const articles = this.draft.articles.map((article, itemIndex) =>
                itemIndex === index ? { ...article, [field]: value } : article
            );
            this.$emit("update-draft", { ...this.draft, articles });
        },
    },
};
</script>

<style scoped>
.review-card, .empty-review { min-height: 620px; }
.review-header { min-height: 92px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); flex-wrap: nowrap; }
.review-body { max-height: calc(100vh - 300px); overflow-y: auto; padding: 0 24px; }
.review-nav { display: flex; align-items: center; gap: 2px; overflow-x: auto; border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
.ownership-bar { display: grid; grid-template-columns: minmax(220px, 1fr) minmax(180px, 240px) minmax(190px, 240px) 24px; align-items: center; gap: 10px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); background: rgba(96, 125, 139, 0.05); }
.conversation-summary { display: flex; align-items: center; min-width: 0; }
.ownership-field { max-width: 240px; }
.review-nav { scrollbar-width: none; }
.review-nav::-webkit-scrollbar { display: none; }
.review-section { padding: 20px 0; }
.section-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.mail-body { white-space: pre-wrap; line-height: 1.55; overflow: hidden; }
.mail-body.collapsed { max-height: 132px; overflow: hidden; }
.evidence-row { display: flex; flex-wrap: wrap; gap: 6px; }
.article-card { border-left: 4px solid #1565c0 !important; }
.prefill-card { border-left: 4px solid #1976d2 !important; }
.delivery-panel ::v-deep .v-expansion-panel-content__wrap { padding: 0; }
.review-actions { min-height: 72px; border-top: 1px solid rgba(0, 0, 0, 0.08); padding: 12px 24px; }
.min-width-0 { min-width: 0; }
.bulk-management .v-input { max-width: 520px; }
@media (max-width: 599px) {
    .review-card, .empty-review { min-height: 480px; }
    .review-header { align-items: flex-start; }
    .review-body { max-height: none; padding: 0 16px; }
    .review-actions { align-items: stretch; flex-direction: column; }
    .review-actions .v-btn { width: 100%; margin-top: 8px; }
    .ownership-bar { grid-template-columns: 1fr; }
    .ownership-field { max-width: none; }
}
</style>
